/**
 * Rotated image collage for the images-with-text-overlay section.
 *
 * The server ships only a <template> of tiles, so nothing downloads until this runs:
 * three rows are built from shuffled copies of those tiles, each row padded to eight so
 * it always outruns the rotated wrapper's width, then the rows drift horizontally with
 * scroll — alternate rows in alternate directions, which is what makes the collage read
 * as depth rather than a single sliding sheet.
 *
 * The drift distance comes from `data-parallax` the way the reference derives it:
 * a factor p travels -p / (1 + p) of a row's width across the section's whole pass
 * through the viewport (-60% at the shipped 1.5).
 *
 * Construction is deferred until the section approaches the viewport, so a visitor who
 * never scrolls this far downloads none of the imagery. Under reduced motion the rows
 * are still built — the collage is composition, not motion — but never moved.
 */
(function () {
  'use strict';

  if (customElements.get('scrolled-images')) return;

  var ROWS = 3;
  var MIN_TILES = 8;

  class ScrolledImages extends HTMLElement {
    connectedCallback() {
      var self = this;
      if (!('IntersectionObserver' in window)) {
        this.build();
        return;
      }
      var observer = new IntersectionObserver(
        function (entries) {
          if (entries.some(function (entry) { return entry.isIntersecting; })) {
            observer.disconnect();
            self.build();
          }
        },
        { rootMargin: '200px' }
      );
      observer.observe(this);
    }

    build() {
      if (this.built) return;
      this.built = true;

      var template = this.querySelector('template');
      var main = this.querySelector('.images-overlay__main');
      if (!template || !main) return;

      var tiles = Array.prototype.slice.call(template.content.children);
      if (tiles.length === 0) return;

      this.rows = [];
      for (var r = 0; r < ROWS; r += 1) {
        var row = document.createElement('div');
        row.className = 'images-overlay__row';
        var order = shuffle(tiles.slice());
        for (var i = 0; i < Math.max(MIN_TILES, order.length); i += 1) {
          row.appendChild(order[i % order.length].cloneNode(true));
        }
        main.appendChild(row);
        this.rows.push(row);
      }

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      var parallax = Number(this.dataset.parallax) || 1.5;
      this.shift = (-parallax * 100) / (1 + parallax);
      this.ticking = false;

      var onScroll = this.requestUpdate.bind(this);
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      this.requestUpdate();
    }

    requestUpdate() {
      if (this.ticking) return;
      this.ticking = true;
      var self = this;
      window.requestAnimationFrame(function () {
        self.ticking = false;
        self.update();
      });
    }

    update() {
      var rect = this.getBoundingClientRect();
      var viewport = window.innerHeight;
      if (rect.bottom < 0 || rect.top > viewport) return;

      // 0 as the section's top meets the viewport's bottom, 1 as its bottom leaves the top.
      var progress = (viewport - rect.top) / (viewport + rect.height);
      progress = Math.min(1, Math.max(0, progress));

      for (var i = 0; i < this.rows.length; i += 1) {
        var x = i % 2 === 0 ? this.shift * (1 - progress) : this.shift * progress;
        this.rows[i].style.transform = 'translateX(' + x + '%)';
      }
    }
  }

  function shuffle(list) {
    for (var i = list.length - 1; i > 0; i -= 1) {
      var j = Math.floor(Math.random() * (i + 1));
      var swap = list[i];
      list[i] = list[j];
      list[j] = swap;
    }
    return list;
  }

  customElements.define('scrolled-images', ScrolledImages);
})();
